/**
 * Security state (GET /api/security): alerts, detection rules, folder format, last backup.
 * One shared store, polled every 10 s while any view that uses it is on screen (Overview's
 * Needs you and the Security page), and refreshed right after every change. In demo mode
 * (?mock=1) it runs on local data.
 */
import { useEffect } from 'react';
import { create } from 'zustand';
import type { DetectionSettings, SecurityState } from '@orgus/protocol';
import { api } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';
import { mockSecurity } from './model.ts';

const POLL_MS = 10_000;

interface SecurityStore {
  state: SecurityState | null;
  loading: boolean;
  error: unknown;
  fetchedAt: number | null;
  set(s: Partial<SecurityStore>): void;
}

const useSecurityStore = create<SecurityStore>()((set) => ({
  state: null,
  loading: true,
  error: null,
  fetchedAt: null,
  set: (s) => set(s),
}));

let users = 0;
let timer: ReturnType<typeof setInterval> | null = null;
const isMock = () => useOrgus.getState().mode === 'mock';

async function refresh(): Promise<void> {
  const st = useSecurityStore.getState();
  if (isMock()) {
    const machines = Object.values(useOrgus.getState().machines);
    if (!st.state && machines.length) st.set({ state: mockSecurity(machines, Date.now()), loading: false, error: null, fetchedAt: Date.now() });
    return;
  }
  try {
    const s = await api<SecurityState>('/api/security');
    st.set({ state: s, loading: false, error: null, fetchedAt: Date.now() });
  } catch (e) {
    st.set({ loading: false, error: e });
  }
}

export interface SecurityApi {
  state: SecurityState | null;
  loading: boolean;
  error: unknown;
  fetchedAt: number | null;
  refresh(): Promise<void>;
  ack(id: string, note: string): Promise<void>;
  saveRules(rules: Partial<DetectionSettings>): Promise<void>;
}

export function useSecurity(): SecurityApi {
  const s = useSecurityStore();
  const mode = useOrgus((x) => x.mode);
  const machineCount = useOrgus((x) => Object.keys(x.machines).length);

  useEffect(() => {
    users++;
    void refresh();
    if (!timer) {
      timer = setInterval(() => {
        if (document.visibilityState === 'visible') void refresh();
      }, POLL_MS);
    }
    return () => {
      users--;
      if (!users && timer) {
        clearInterval(timer);
        timer = null;
      }
    };
  }, []);

  // Demo data needs the demo machines; switching modes starts over.
  useEffect(() => {
    useSecurityStore.getState().set({ state: null, loading: true, error: null });
    void refresh();
  }, [mode]);
  useEffect(() => {
    if (isMock() && !useSecurityStore.getState().state && machineCount) void refresh();
  }, [machineCount]);

  const accept = (next: SecurityState) => s.set({ state: next, error: null, fetchedAt: Date.now() });

  return {
    state: s.state,
    loading: s.loading,
    error: s.error,
    fetchedAt: s.fetchedAt,
    refresh,
    ack: async (id, note) => {
      if (isMock()) {
        const cur = useSecurityStore.getState().state;
        if (cur) accept({ ...cur, alerts: cur.alerts.map((a) => (a.id === id ? { ...a, status: 'acknowledged', ack: { by: 'owner', at: Date.now(), note } } : a)) });
        return;
      }
      accept(await api<SecurityState>(`/api/security/alerts/${encodeURIComponent(id)}/ack`, { method: 'POST', body: { note } }));
    },
    saveRules: async (rules) => {
      if (isMock()) {
        const cur = useSecurityStore.getState().state;
        if (cur) accept({ ...cur, rules: { ...cur.rules, ...rules } as DetectionSettings });
        return;
      }
      accept(await api<SecurityState>('/api/security/rules', { method: 'PUT', body: rules }));
    },
  };
}
